import React, {useEffect, useState} from "react";
import {changeStatus, create, getById, update} from "../../services/cash-box.service";
import {deleteById} from "../../services/purchase.service";
import {NavigateFunction, NavLink, useNavigate, useParams} from "react-router-dom";
import {useFormik} from 'formik';
import {Button as MaterialButton, Chip, Chip as ChipMaterial, Link, TextField, Tooltip} from "@mui/material";
import {DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams, GridToolbarContainer} from "@mui/x-data-grid";
import {ArrowBackIosNew} from "@mui/icons-material";
import showNotification from "../utils/Notify";
import {Box, Breadcrumbs, Button, Grid, Stack, Typography} from "@mui/joy";
import {Experimental_CssVarsProvider as CssVarsProvider} from "@mui/material/styles";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ICashBox from "../../types/cash-box.type";
import {CashBoxStatus} from "../../types/enumeration/CashBoxStatus";
import {CashBoxType} from "../../types/enumeration/CashBoxType";
import {CashBoxTypeNames} from "../../types/enumeration/CashBoxTypeNames";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import moment from "moment/moment";
import {CashBoxStatusColors} from "../../types/enumeration/CashBoxStatusColors";
import {CashBoxStatusNames} from "../../types/enumeration/CashBoxStatusNames";
import ConfirmationModal from "../utils/ConfirmationModal";
import AddIcon from "@mui/icons-material/Add";
import IPurchase from "../../types/purchase.type";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import {PurchaseApprovalStatus} from "../../types/enumeration/PurchaseApprovalStatus";
import {PurchaseApprovalStatusNames} from "../../types/enumeration/PurchaseApprovalStatusNames";
import {PurchaseApprovalStatusColors} from "../../types/enumeration/PurchaseApprovalStatusColors";
import StatusHistoryModal from "../utils/StatusHistoryModal";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ICashBoxChangeStatus from "../../types/cash-box-change-status.type";

export const MyCashBoxForm: React.FC = () => {
  let navigate: NavigateFunction = useNavigate();
  const { cashBoxId } = useParams();
  const [selectedNewStatus, setSelectedNewStatus] = React.useState<CashBoxStatus | null>(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = React.useState(false);
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = React.useState(false);
  const [modalTitle, setModalTitle] = React.useState("");
  const [modalMessage, setModalMessage] = React.useState("");
  const [confirmationDeleteModalOpen, setConfirmationDeleteModalOpen] = React.useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = React.useState<any>();

  const initialValues: ICashBox = {
    id: null,
    amount: 0,
    createdBy: null,
    assignedWorker: null,
    comments: "",
    created: null,
    serviceOrder: null,
    status: CashBoxStatus.IN_PROGRESS,
    type: CashBoxType.CASH,
    purchases: [],
    statusHistory: []
  };

  const [cashBox, setCashBox] = useState<ICashBox>(initialValues);

  const onSubmit = (cashBox: ICashBox) => {
    if (cashBoxId) {
      update(cashBox.id, cashBox)
        .then(() => {
          showNotification({type: 'success', message: 'Caja acutalidaza correctamente.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    } else {
      create(cashBox)
        .then((response) => {
          showNotification({type: 'success', message: 'Caja creada correctamente.'});
          goBack();
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit
  });

  const goBack = () => {
    navigate(-1)
  };

  const columns: GridColDef<IPurchase>[] = [
    {
      field: 'invoiceNumber',
      headerName: 'Factura Numero',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'created',
      headerName: 'Fecha',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{`${moment(params.row.created).format('DD/MM/YYYY')}`}</span>;
      }
    },
    {
      field: 'invoiceType',
      headerName: 'Tipo',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >{params.row.invoiceType?.name}</span>;
      }
    },
    {
      field: 'approvalStatus',
      headerName: 'Estado',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <Chip color={PurchaseApprovalStatusColors[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     label={PurchaseApprovalStatusNames[PurchaseApprovalStatus[params.row.approvalStatus as keyof typeof PurchaseApprovalStatus]]}
                     sx={{width: 160, fontWeight: 'bold', height: 20, marginBottom: 0.5}}/>;
      }
    },
    {
      field: 'totalAmount',
      headerName: 'Monto Total',
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      minWidth:160,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return <span >${`${params.row.totalAmount.toFixed(2)}`}</span>;
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      headerAlign: 'center',
      align: 'center',
      minWidth: 20,
      flex: 1,
      cellClassName: 'actions',
      getActions: (params) => {
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(params.row)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteForeverIcon />}
            label="Delete"
            className="textPrimary"
            onClick={handleDeleteClick(params.row)}
            color="inherit"
          />
        ];
      },
    },
  ];

  const handleEditClick = (purchase: IPurchase) => () => {
    navigate('purchase/edit/' + purchase.id, { state: { serviceOrder: formik.values.serviceOrder?.id}});
  };

  const handleAddClick = () => {

    navigate('purchase/create', { state: { serviceOrder: formik.values.serviceOrder?.id}});
  };

  const handleDeleteClick = (purchase: IPurchase) => () => {
    setSelectedPurchaseId(purchase.id);
    setConfirmationDeleteModalOpen(true);
  };

  const handleStatusHistoryModalOpen = () => {
    setStatusHistoryModalOpen(true);
  }

  const handleStatusHistoryModalClose = () => {
    setStatusHistoryModalOpen(false);
  }

  const handleConfirmationDeleteModalClose = () => {
    setSelectedPurchaseId(null);
    setConfirmationDeleteModalOpen(false);
  }

  const onConfirmDelete = () => {
    deleteById(selectedPurchaseId)
      .then(() => {
        showNotification({type: 'success', message: 'Compra eliminada correctamente.'});
        handleConfirmationDeleteModalClose();
        fetchCashBox()
      })
      .catch(error => {
        showNotification({type: 'error', message: error.response.data.message});
      });
  };

  function EditToolbar() {
    return (
      <GridToolbarContainer sx={{paddingBottom: 1}}>
        <Box sx={{ flexGrow: 1 }} />
        <MaterialButton
          variant="outlined"
          endIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={formik.values.status === CashBoxStatus.SENT}
        >
          Agregar Compra
        </MaterialButton>
      </GridToolbarContainer>
    );
  }

  const onConfirm = (comments?: string) => {
    if (selectedNewStatus) {
      let newStatus: ICashBoxChangeStatus = {
        status: selectedNewStatus,
        comments: comments ? comments : '',
      };
      changeStatus(cashBox.id, newStatus)
        .then((response) => {
          if (selectedNewStatus === CashBoxStatus.CONFIRM) {
            showNotification({type: 'success', message: "Caja Confirmada correctamente."});
            fetchCashBox();
          } else {
            goBack();
            showNotification({type: 'success', message: "Rendicion de Caja enviada correctamente."});
          }
        })
        .catch(error => {
          showNotification({type: 'error', message: error.response.data.message});
        });
    }

    setSelectedNewStatus(null);
    setConfirmationModalOpen(false);
  };

  const handleConfirmationModalClose = () => {
    setSelectedNewStatus(null);
    setConfirmationModalOpen(false);
  }

  const handleOpenChangeStatusModal = (status: CashBoxStatus) => {
    if (status === CashBoxStatus.CONFIRM) {
      setModalTitle("Confirmar Caja");
      setModalMessage("Se Confirmara la Caja y se habilitara la carga de facturas.")
    } else {
      setModalTitle("Enviar Rendicion De Caja");
      setModalMessage("Al confirmar esta accion se enviara la rendicion de caja. ")
    }
    setSelectedNewStatus(status);
    setConfirmationModalOpen(true);
  };

  const fetchCashBox = async () => {
    if (cashBoxId) {
      getById(cashBoxId).then( (response) => {
        let cashBoxData = response.data;
        setCashBox(cashBoxData)
        formik.setValues({
          ...cashBoxData,
          created: moment(cashBoxData.created)
        });
      })
    }
  };

  useEffect(() => {
    fetchCashBox();
  }, []);

  return (
    <Box
      sx={{
        px: { xs: 2},
        pt: { xs: 'calc(12px + var(--Header-height))' },
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100dvh',
        overflow: "auto"
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 1 },
              width: '100%',
            }}
          >
            <Breadcrumbs
              size="sm"
              aria-label="breadcrumbs"
              separator={<ChevronRightRoundedIcon/>}
              sx={{ pl: 0 }}
            >
              <Typography level="h4" component="h1">
                <LocalAtmIcon fontSize="small" sx={{ fontSize: 30, marginTop: -1}} />
              </Typography>
              <Typography level="h4" component="h1">
                <Link component={NavLink} to='/my-cash-boxes' underline={"none"}>Mis Cajas</Link>
              </Typography>
              <Typography level="h4" component="h1">
                Rendicion de Caja
              </Typography>
            </Breadcrumbs>
            <Box>
              <CssVarsProvider>
                <ChipMaterial
                  color={CashBoxStatusColors[CashBoxStatus[formik.values.status as keyof typeof CashBoxStatus]]}
                  label={CashBoxStatusNames[CashBoxStatus[formik.values.status as keyof typeof CashBoxStatus]]}
                  sx={{width: 200, fontWeight: 'bold', fontSize: 15}}
                  icon={<ManageSearchIcon fontSize="medium" sx={{marginTop: -0.5}} />}
                  clickable={true}
                  onClick={() => handleStatusHistoryModalOpen()}
                />
              </CssVarsProvider>
            </Box>
          </Box>

          <ConfirmationModal
            open={confirmationModalOpen}
            onConfirm={(comments?: string) => { onConfirm(comments)}}
            onCancel={handleConfirmationModalClose}
            title={modalTitle}
            message={modalMessage}
            isDeleteAction={false}
            enableComments={true}
          />

          <ConfirmationModal
            open={confirmationDeleteModalOpen}
            onConfirm={onConfirmDelete}
            onCancel={handleConfirmationDeleteModalClose}
            title="Confirmacion"
            message="Esta seguro de eliminar esta compra?"
            isDeleteAction={true}
          />

          <StatusHistoryModal
            open={statusHistoryModalOpen}
            title={"Historial de Estados de Caja"}
            history={cashBox.statusHistory}
            onCancel={handleStatusHistoryModalClose}
            historyType={"cashBox"}
          />

          <Box sx={{ width: '100%', typography: 'body1' }}>
            <form className="form" onSubmit={formik.handleSubmit}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                marginBlock={1}
                marginInline={0}
                sx={{ width: '100%' }}
              >
                <Grid xl={6} md={6} sm={6} xs={6} container rowSpacing={2} columnSpacing={2}>

                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <TextField
                        fullWidth
                        id="serviceOrder"
                        label="Orden de Servicio"
                        name="serviceOrder"
                        variant="filled"
                        type={"text"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.serviceOrder ? formik.values.serviceOrder.number : ''}
                        slotProps={{ inputLabel: {shrink: true}, input: {readOnly: true}}}
                      />
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <TextField
                        fullWidth
                        id="created"
                        label="Fecha"
                        name="created"
                        variant="filled"
                        type={"text"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={moment(formik.values.created).format('DD/MM/YYYY')}
                        slotProps={{ inputLabel: {shrink: true}, input: {readOnly: true}}}
                      />
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={12} md={12} sm={12} xs={12}>
                    <CssVarsProvider>
                      <TextField
                        fullWidth
                        id="type"
                        label="Tipo"
                        name="type"
                        variant="filled"
                        type={"text"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={CashBoxTypeNames[CashBoxType[formik.values.type as keyof typeof CashBoxType]]}
                        slotProps={{ inputLabel: {shrink: true}, input: {readOnly: true}}}
                      />
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={6} md={6} sm={6} xs={6}>
                    <CssVarsProvider>
                      <TextField
                        fullWidth
                        id="amount"
                        label="Monto"
                        name="amount"
                        variant="filled"
                        type={"number"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.amount}
                        slotProps={{ inputLabel: {shrink: true}, input: {readOnly: true}}}
                      />
                    </CssVarsProvider>
                  </Grid>

                  <Grid xl={6} md={6} sm={6} xs={6}>
                    <CssVarsProvider>
                      <Tooltip title={"Sumatoria de todas las compras cargadas a la caja."} placement="top">
                        <TextField
                          fullWidth
                          id="amount-surrendered"
                          label="Monto Consumido"
                          name="amount-surrendered"
                          variant="filled"
                          type={"number"}
                          value={cashBox.purchases?.reduce((sum, purchase) => sum + purchase.totalAmount, 0) ?? 0}
                          slotProps={{ inputLabel: {shrink: true}, input: {readOnly: true}}}
                        />
                      </Tooltip>
                    </CssVarsProvider>
                  </Grid>
                </Grid>

                <Grid xl={6} md={6} sm={6} xs={6} >
                  <CssVarsProvider>
                    <TextField
                      fullWidth
                      id="comments"
                      label="Comentarios"
                      name="comments"
                      variant="filled"
                      type={"text"}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.comments}
                      error={!!(formik.touched.comments && formik.errors.comments)}
                      slotProps={{ inputLabel: {shrink: true}, input: {readOnly: true}}}
                      multiline
                      rows={11}
                      sx={{mb: '3px'}}
                    />
                  </CssVarsProvider>
                </Grid>

              </Grid>

              <Box sx={{ width: '100%', height: 384, typography: 'body1' }} ml={1} mb={2} pr={2}>
                <CssVarsProvider>
                  <DataGrid
                    slots={{toolbar: EditToolbar}}
                    rows={cashBox.purchases}
                    columns={columns}
                    rowHeight={25}
                    columnHeaderHeight={30}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 10,
                        },
                      },
                    }}
                    pageSizeOptions={[10]}
                    localeText={{ noRowsLabel: "No Hay Compras Registradas" }}
                    disableRowSelectionOnClick
                  />
                </CssVarsProvider>
              </Box>

              <Stack direction="row" justifyContent="flex-end" spacing={2}  pr={1} pl={1}>
                <Button onClick={() => goBack()} variant="outlined" endDecorator={<ArrowBackIosNew />}>Volver</Button>
                {cashBoxId && formik.values.status === CashBoxStatus.SENT &&
                  <Button
                    onClick={() => handleOpenChangeStatusModal(CashBoxStatus.CONFIRM)}
                    variant="solid"
                    color="primary"
                    endDecorator={<CheckCircleIcon />}
                  >
                    Confirmar Caja
                  </Button>
                }
                {cashBoxId && (formik.values.status === CashBoxStatus.CONFIRM || formik.values.status === CashBoxStatus.TO_FIX) &&
                  <Button
                    onClick={() => handleOpenChangeStatusModal(CashBoxStatus.REPORT_SUBMITTED)}
                    variant="solid"
                    color="primary"
                    endDecorator={<CheckCircleIcon />}
                  >
                    Enviar Rendicion De Caja
                  </Button>
                }
              </Stack>

            </form>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};


